import React from "react";
import { useNavigate, useParams } from "react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2,
  Database,
  FileMinus,
  ListChecks,
  ListRestart,
  Users,
  Tag,
  BriefcaseBusiness,
  LayoutDashboard,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  Monitor,
  Flag,
  ScrollText,
  Scale,
  FileText,
  Inbox,
  Files,
  UserCog,
  FileStack,
  LayoutTemplate,
  FolderOpen,
  FolderKanban,
  ChevronsUpDown,
  LogOut,
  KeyRound,
  Moon,
  Sun,
  Palette,
  FolderSymlink,
  Unlink,
  BookOpen,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useThemeVariant, THEME_VARIANTS } from "../../contexts/ThemeVariantContext";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole, UserRoleColors, UserRoleLabels } from "../../models/User";
import { colorVariantClasses } from "../../theme/AppColorVariant";
import GetDocumentsTemporaryAccessModal from "./GetTemporaryAccessModal";
import { AccessData } from "../../pages/DashboardPage";
import AccessTimer from "./AccessTimer";
import { NAVIGATION_KEYS } from "../../constants/NavigationKeys";
import { conflictRulePagePath } from "../../models/ConflictRule";

interface AppSidebarProps {
  selectedItem?: string;
  accessData?: AccessData | null;
  userRole: UserRole;
  userName: string;
  isAssignedUser: boolean;
  isLoading?: boolean;
  currentOrganizationIntactId?: string;
  currentOrganizationName?: string;
  /** Changelog releases the user has not opened yet; badged on the Docs item. */
  unseenReleases?: number;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  selectedItem,
  accessData,
  userRole,
  userName,
  isAssignedUser,
  isLoading,
  currentOrganizationIntactId,
  currentOrganizationName,
  unseenReleases = 0,
}) => {
  const { organizationId } = useParams<{ organizationId?: string }>();
  const navigate = useNavigate();
  const { state: sidebarState } = useSidebar();

  const isLoadingAccess = accessData === null && organizationId !== undefined;

  const defaultOrganizationMenuItems = [
    { keys: [NAVIGATION_KEYS.OVERVIEW], label: "Overview", icon: LayoutDashboard, path: (id: string) => `/organization/${id}` },
    { keys: [NAVIGATION_KEYS.DISCOVERY_CYCLES], label: "Discovery Cycles", icon: RefreshCw, path: (id: string) => `/organization/${id}/discovery-cycles` },
    { keys: [NAVIGATION_KEYS.ORGANIZATION_CONFIGURATION], label: "Organization Configuration", icon: Settings, path: (id: string) => `/organization/${id}/configuration` },
    { keys: [NAVIGATION_KEYS.CLIENT_CONFIGURATIONS, NAVIGATION_KEYS.CLIENT_CONFIGURATION], label: "Client Configurations", icon: SlidersHorizontal, path: (id: string) => `/organization/${id}/client-configurations` },
    { keys: [NAVIGATION_KEYS.CLIENTS], label: "Clients", icon: Monitor, path: (id: string) => `/organization/${id}/clients` },
    { keys: [NAVIGATION_KEYS.FLAGGED_PATHS], label: "Flagged Paths", icon: Flag, path: (id: string) => `/organization/${id}/flagged-paths`, requiresDocumentAccess: true },
    { keys: [NAVIGATION_KEYS.RENAMED_FOLDERS], label: "Renamed Folders", icon: FolderSymlink, path: (id: string) => `/organization/${id}/renamed-folders`, requiresDocumentAccess: true },
    { keys: [NAVIGATION_KEYS.SOURCE_LINKS], label: "Source Links", icon: Unlink, path: (id: string) => `/organization/${id}/source-links`, requiresDocumentAccess: true },
    { keys: [NAVIGATION_KEYS.AUDIT_LOGS], label: "Audit Logs", icon: ScrollText, path: (id: string) => `/organization/${id}/audit-logs` },
  ];

  const documentOrganizationMenuItems = [
    { keys: [NAVIGATION_KEYS.CASES, NAVIGATION_KEYS.CASE], label: "Cases", icon: Scale, path: (id: string) => `/organization/${id}/cases` },
    { keys: [NAVIGATION_KEYS.CASE_FILES], label: "Case Files", icon: FileText, path: (id: string) => `/organization/${id}/case-files` },
    { keys: [NAVIGATION_KEYS.INTAKES, NAVIGATION_KEYS.INTAKE], label: "Intakes", icon: Inbox, path: (id: string) => `/organization/${id}/intakes` },
    { keys: [NAVIGATION_KEYS.INTAKE_FILES], label: "Intake Files", icon: Files, path: (id: string) => `/organization/${id}/intake-files` },
    { keys: [NAVIGATION_KEYS.PROVIDERS, NAVIGATION_KEYS.PROVIDER], label: "Providers", icon: UserCog, path: (id: string) => `/organization/${id}/providers` },
    { keys: [NAVIGATION_KEYS.PROVIDER_FILES], label: "Provider Files", icon: FileStack, path: (id: string) => `/organization/${id}/provider-files` },
    { keys: [NAVIGATION_KEYS.TEMPLATES, NAVIGATION_KEYS.TEMPLATE], label: "Templates", icon: LayoutTemplate, path: (id: string) => `/organization/${id}/templates` },
    { keys: [NAVIGATION_KEYS.TEMPLATE_FILES], label: "Template Files", icon: Files, path: (id: string) => `/organization/${id}/template-files` },
    { keys: [NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDERS, NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDER], label: "Firm Doc Folders", icon: FolderKanban, path: (id: string) => `/organization/${id}/firm-documents-folders` },
    { keys: [NAVIGATION_KEYS.FIRM_DOCUMENTS], label: "Firm Documents", icon: FolderOpen, path: (id: string) => `/organization/${id}/firm-documents` },
  ];

  const managementToolsMenuItems = [
    { keys: [NAVIGATION_KEYS.EXECUTE_SQL_SCRIPTS], label: "Execute SQL Scripts", tooltip: "Execute SQL Scripts", icon: Database, path: (id: string) => `/organization/${id}/execute-sql-scripts` },
    { keys: [NAVIGATION_KEYS.RECALCULATE_FILE_PRIORITIES], label: "Refresh Files Migration Queue", tooltip: "Recompute the order files migrate in — run after changing file or case statuses (e.g. via a SQL script)", icon: ListRestart, path: (id: string) => `/organization/${id}/recalculate-file-priorities` },
    { keys: [NAVIGATION_KEYS.BULK_DELETE], label: "Bulk Delete", tooltip: "Upload a list of database ids and delete what they name, along with their SharePoint copies", icon: FileMinus, path: (id: string) => `/organization/${id}/bulk-delete` },
    { keys: [NAVIGATION_KEYS.RESOLVE_CONFLICTS_BY_RULE], label: "Resolve Conflicts by Rule", tooltip: "Pick the winner of many conflict groups at once with a rule, preview it, then run it", icon: ListChecks, path: (id: string) => conflictRulePagePath(id) },
  ];

  const { resolvedTheme, setTheme } = useTheme();
  const { variant, setVariant } = useThemeVariant();
  const { logout } = useAuth();

  const toggleTheme = () => setTheme(resolvedTheme === "dark" ? "light" : "dark");
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const roleColors = colorVariantClasses[UserRoleColors[userRole]];
  const accessLevelLabel = userRole === UserRole.Admin
    ? "Admin"
    : accessData?.isTemporary
      ? "Temporary access"
      : isAssignedUser
        ? "Primary user"
        : "Guest";

  return (
    <Sidebar collapsible="icon" className="overflow-hidden">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" tooltip="Neos File Sync">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-sidebar-accent text-sidebar-accent-foreground shrink-0 text-xs font-bold">
                NFS
              </div>
              <span className="text-sm font-semibold text-sidebar-foreground tracking-tight">Neos File Sync</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Dashboard group */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={!organizationId && selectedItem === "Organizations"}
                  onClick={() => navigate("/organizations")}
                  tooltip="Organizations"
                >
                  <Building2 />
                  <span>Organizations</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {userRole === UserRole.Admin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={selectedItem === "Users"}
                    onClick={() => navigate("/users")}
                    tooltip="Users"
                  >
                    <Users />
                    <span>Users</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {(userRole === UserRole.Admin || userRole === UserRole.Editor) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={selectedItem === "Versions"}
                    onClick={() => navigate("/versions")}
                    tooltip="Versions"
                  >
                    <Tag />
                    <span>Versions</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {(userRole === UserRole.Admin || userRole === UserRole.Editor) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={selectedItem === "Jobs"}
                    onClick={() => navigate("/jobs")}
                    tooltip="Jobs"
                  >
                    <BriefcaseBusiness />
                    <span>Jobs</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={selectedItem === NAVIGATION_KEYS.DOCS}
                  // While the count shows, Docs opens the changelog it counts, which also clears it.
                  onClick={() => {
                    const docsBase = organizationId ? `/organization/${organizationId}/docs` : "/docs";
                    navigate(unseenReleases > 0 ? `${docsBase}/changelog` : docsBase);
                  }}
                  tooltip={unseenReleases > 0 ? `Docs — ${unseenReleases} new release${unseenReleases === 1 ? "" : "s"}` : "Docs"}
                >
                  <BookOpen />
                  <span>Docs</span>
                </SidebarMenuButton>
                {unseenReleases > 0 && (
                  <SidebarMenuBadge className="bg-primary text-primary-foreground peer-hover/menu-button:text-primary-foreground peer-data-[active=true]/menu-button:text-primary-foreground">
                    {unseenReleases > 9 ? "9+" : unseenReleases}
                  </SidebarMenuBadge>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Organization group */}
        {organizationId && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    size="lg"
                    className="cursor-default hover:bg-transparent"
                    tooltip={`${currentOrganizationIntactId} – ${currentOrganizationName}\nAccess: ${accessLevelLabel}`}
                  >
                    {sidebarState === "collapsed" && (
                      <div className={cn("flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold shrink-0", roleColors.bg, roleColors.text)}>
                        {(currentOrganizationName || "?").substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="grid flex-1 text-left leading-tight overflow-hidden">
                      <span className="truncate text-sm font-semibold text-sidebar-foreground">{currentOrganizationIntactId} – {currentOrganizationName}</span>
                      <span className="text-xs text-sidebar-foreground/60">
                        Access level – <span className={cn("font-medium", roleColors.text)}>{accessLevelLabel}</span>
                      </span>
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
              <SidebarGroupContent>
                <SidebarMenu>
                  {isLoadingAccess || isLoading
                    ? Array.from({ length: 7 }).map((_, i) => (
                        <SidebarMenuItem key={i}>
                          <SidebarMenuSkeleton showIcon />
                        </SidebarMenuItem>
                      ))
                    : defaultOrganizationMenuItems
                        // The route guard sends anyone without document access back to Overview, so the entry would only bounce.
                        .filter((item) => !item.requiresDocumentAccess || accessData?.hasAccess)
                        .map((item, i) => (
                        <SidebarMenuItem key={i}>
                          <SidebarMenuButton
                            isActive={(item.keys as string[]).includes(selectedItem ?? "")}
                            onClick={() => navigate(item.path(organizationId))}
                            tooltip={item.label}
                          >
                            <item.icon />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Documents group */}
            <SidebarGroup>
              <SidebarGroupLabel>Documents</SidebarGroupLabel>
              <SidebarGroupContent>
                {!accessData?.hasAccess && (
                  <div className="px-2 pb-1">
                    <GetDocumentsTemporaryAccessModal>
                      <Button size="sm" className="w-full">
                        Get Access
                      </Button>
                    </GetDocumentsTemporaryAccessModal>
                  </div>
                )}

                {accessData?.hasAccess && accessData?.isTemporary && (
                  <div className="px-2 pb-1">
                    <AccessTimer
                      endDate={accessData.endDate}
                      hasAccess={accessData.hasAccess}
                      isTemporary={accessData.isTemporary}
                    />
                  </div>
                )}

                {accessData?.hasAccess && (
                  <SidebarMenu>
                    {documentOrganizationMenuItems.map((item, i) => (
                      <SidebarMenuItem key={i}>
                        <SidebarMenuButton
                          isActive={(item.keys as string[]).includes(selectedItem ?? "")}
                          onClick={() => navigate(item.path(organizationId))}
                          tooltip={item.label}
                        >
                          <item.icon />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                )}
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Management Tools group: the server refuses every one of these without document access to the organization. */}
            {(userRole === UserRole.Admin || userRole === UserRole.Editor) && accessData?.hasAccess && (
              <SidebarGroup>
                <SidebarGroupLabel>Management Tools</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {managementToolsMenuItems.map((item, i) => (
                      <SidebarMenuItem key={i}>
                        <SidebarMenuButton
                          isActive={(item.keys as string[]).includes(selectedItem ?? "")}
                          onClick={() => navigate(item.path(organizationId))}
                          tooltip={item.tooltip}
                        >
                          <item.icon />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="rounded-lg">
                    <AvatarFallback className={cn("rounded-lg", roleColors.bg, roleColors.text)}>
                      {userName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{userName}</span>
                    <span className={cn("truncate text-xs", roleColors.text)}>{UserRoleLabels[userRole]}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="end"
                sideOffset={4}
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5">
                    <Avatar className="rounded-lg">
                      <AvatarFallback className={cn("rounded-lg", roleColors.bg, roleColors.text)}>
                        {userName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{userName}</span>
                      <span className={cn("truncate text-xs", roleColors.text)}>{UserRoleLabels[userRole]}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={toggleTheme}>
                  {resolvedTheme === "dark" ? <Sun /> : <Moon />}
                  {resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Palette />
                    Theme
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup value={variant} onValueChange={(v) => setVariant(v as typeof variant)}>
                      {THEME_VARIANTS.map((t) => (
                        <DropdownMenuRadioItem key={t.value} value={t.value}>
                          {t.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem onClick={() => navigate("/change-password")}>
                  <KeyRound />
                  Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={handleLogout}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
